import { Add, ArrowUpRight, ChevronDown, Edit, EditOff, Filter, Logout, Pause, Play, Reset, Save, TrashCan } from '@carbon/icons-react';
import { Avatar, Button, Divider, FormControl, Menu, MenuItem, Select, useTheme } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Badge from '@mui/material/Badge';
import Fade from '@mui/material/Fade';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import i18next from 'i18next';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { distinctUntilChanged } from 'rxjs/operators';
import * as uuid from 'uuid';
import useInterval from '../../hooks/useInterval';
import { useStream } from '../../hooks/useStream';
import {
    ConfirmDialogModeEnum,
    EditorModeEnum,
    LoginSettings,
    RoleEnum,
    SingleValueStyles,
    Stage,
    StageHeader,
    StageOperationEnum,
    StageStatusEnum,
    StageViewModeEnum,
} from '../../models/Index';
import DoormanService from '../../services/doormanService/DoormanService';
import SettingsService from '../../services/settingsService/SettingsService';
import StageService from '../../services/stageService/StageService';
import StateService from '../../services/stateService/StateService';
import UserService from '../../services/userService/UserService';
import UtilService from '../../services/utilService/UtilService';
import BlockUi from '../block-ui/BlockUi';
import ThemeToggle from '../globalHeader/themeToggle/ThemeToggle';
import RoleChange from '../role-change/RoleChange';
import StageHeaderTab from '../stage-header-tab/StageHeaderTab';
import StageHeaderTabs from '../stage-header-tabs/StageHeaderTabs';
import { useStyles } from './styles';

function Header() {
    const theme = useTheme();
    const classes = useStyles();
    const [editorMode, setEditorMode] = useState(EditorModeEnum.CLOSED as EditorModeEnum);
    const [rolesPresent, setRolesPresent] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [savedStage, setSavedStage] = useState({} as Stage);
    const [selectedStage, setSelectedStage] = useStream(StateService.selectedStage$);
    const [selectedStageHeader, setSelectedStageHeader] = useState({} as StageHeader);
    const [stageHeaders, setStageHeaders] = useState([] as StageHeader[]);
    const [confirmDialogMode, setConfirmDialogMode] = useState(ConfirmDialogModeEnum.CLOSED);
    const [unsavedChangesNewIndex, setUnsavedChangesNewIndex] = useState(-1);
    const [unsavedChangesTriggeredBy, setUnsavedChangesTriggeredBy] = useState('');
    const [currentCustomerUuid, setCurrentCustomerUuid] = useStream(StateService.currentCustomerUuid$);
    const [disableButtons, setDisableButtons] = useState(true);
    const [t] = useTranslation();
    const [stageViewMode, setStageViewMode] = useState(StageViewModeEnum.PUBLISHED as StageViewModeEnum);
    const [currentRoles, setCurrentRoles] = useState([] as RoleEnum[]);
    const [playStageSwitch, setPlayStageSwitch] = useState(false);
    const [searchString, setSearchString] = useState('');
    const [previousSearchString, setPreviousSearchString] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [stageHeader, setStageHeader] = useState([] as StageHeader[]);
    const [selectedHeader, setSelectedHeader] = useState({} as StageHeader);
    const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
    const [savedChanges, setSavedChanges] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const abortController = new AbortController();
    const [menuOpen, setMenuOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const getContentAnchorEl = null;
    const bodyTheme = localStorage.getItem('theme') || 'light';
    const [event, setEvent] = useState(false);
    const [showViewerHeader, setShowViewerHeader] = useState(false);
    const [playStageSwitchViewer, setPlayStageSwitchViewer] = useState(true);
    const [loginSettings, setLoginSettings] = useState({} as LoginSettings);
    const [initials, setInitials] = useState('');
    const localStorageLang = localStorage.getItem('language') || 'de';
    const [currentLanguage, setCurrentLanguage] = useState(i18next.language);
    const [newLanguage, setNewLanguage] = useState(i18next.language);
    const [currentDuration, setCurrentDuration] = useState(30000);
    const [isLoginSettingsSaving, setIsLoginSettingsSaving] = useState(false);
    const [latestDuration, setLatestDuration] = useState(loginSettings.duration || 30000);
    const [isChangeMade, setIsChangeMade] = useState(false);
    const [widgets, setWidgets] = useStream(StateService.widgets$, distinctUntilChanged());
    const [widgetData, setWidgetData] = useStream(StateService.widgetData$, distinctUntilChanged());

    enum TriggerEnum {
        ADD_STAGE = 'addStage',
        SWITCH_STAGE = 'switchStage',
    }

    useEffect(() => {
        const confirmDialogMode$ = StateService.confirmDialogMode$.subscribe((mode) => {
            setConfirmDialogMode(mode);
        });

        const currentRoles$ = StateService.currentRoles$.subscribe((currentRoles) => {
            setCurrentRoles(currentRoles);
        });

        const editorMode$ = StateService.editorMode$.subscribe((editorMode) => {
            setEditorMode(editorMode);
        });

        const savedStage$ = StateService.savedStage$.subscribe((stage) => {
            setSavedStage(stage);
        });

        const selectedHeader$ = StateService.selectedHeader$
            .pipe(
                distinctUntilChanged(
                    (prev: StageHeader, curr: StageHeader) => prev?.draftId === curr?.draftId && prev?.stageId === curr?.stageId,
                ),
            )
            .subscribe((selected) => {
                setSelectedStageHeader(selected);
            });

        const selectedStageHeader$ = StateService.selectedHeader$.subscribe((selectedHeader) => {
            setSelectedHeader(selectedHeader);
        });

        const stageHeader$ = StateService.stageHeader$.subscribe((headers) => {
            const array = StageService.sortHeaderArrayAlphabetically(headers);
            setStageHeaders(array);
            setStageHeader(headers);
        });

        const stageViewMode$ = StateService.stageViewMode$.subscribe((mode) => {
            setStageViewMode(mode);
        });

        const loginSettings$ = StateService.loginSettings$.subscribe((loginSettings) => {
            setLoginSettings(loginSettings);
        });

        const changesMade$ = StateService.changesMade$.subscribe((changesMade) => {
            setIsChangeMade(changesMade);
        });

        return () => {
            confirmDialogMode$.unsubscribe();
            currentRoles$.unsubscribe();
            editorMode$.unsubscribe();
            savedStage$.unsubscribe();
            selectedHeader$.unsubscribe();
            stageHeader$.unsubscribe();
            stageViewMode$.unsubscribe();
            selectedStageHeader$.unsubscribe();
            abortController.abort();
            loginSettings$.unsubscribe();
            changesMade$.unsubscribe();
        };
    }, []);

    useEffect(() => {
        async function getData() {
            if (currentCustomerUuid && currentCustomerUuid.length > 0) {
                setIsLoading(true);
                await getStageHeader(currentCustomerUuid);
                setIsLoading(false);
                setDisableButtons(false);
            } else {
                setDisableButtons(true);
            }
        }

        getData();
    }, [currentCustomerUuid]);

    const filteredStageHeaders = useMemo(() => {
        let filtered = [] as StageHeader[];
        if (searchString === '' && stageHeaders.length > 0) {
            filtered = stageHeaders;
        } else {
            filtered = stageHeaders.filter(({ title }) => title.toLowerCase().includes(searchString));
        }
        setTabValue(0);
        return filtered;
    }, [searchString, stageHeaders]);

    useEffect(() => {
        if (filteredStageHeaders && ![EditorModeEnum.NEW_STAGE].includes(editorMode)) {
            if (filteredStageHeaders.indexOf(selectedStageHeader) === -1 && filteredStageHeaders.length === 0) {
                StateService.selectedHeader$.next({} as StageHeader);
            } else if (filteredStageHeaders.indexOf(selectedStageHeader) === -1 && filteredStageHeaders.length > 0) {
                const newHeader = filteredStageHeaders[0] || ({} as StageHeader);
                StateService.selectedHeader$.next(newHeader);
            }
            const index = filteredStageHeaders.indexOf(selectedStageHeader) > -1 ? filteredStageHeaders.indexOf(selectedStageHeader) : 0;
            setTabValue(index);
        }
    }, [selectedStageHeader, filteredStageHeaders]);

    useEffect(() => {
        if (confirmDialogMode === ConfirmDialogModeEnum.UNSAVED_CHANGES_CONFIRMED) {
            delegate(unsavedChangesTriggeredBy, unsavedChangesNewIndex);
        }
    }, [confirmDialogMode]);

    useEffect(() => {
        setRolesPresent(currentRoles.length > 0 && currentRoles[0].length > 0 ? true : false);
    }, [currentRoles]);

    useInterval(
        () => {
            if (filteredStageHeaders.length > 0) {
                switchTab(null, tabValue === filteredStageHeaders.length - 1 ? 0 : tabValue + 1);
            }
        },
        !playStageSwitch ? null : currentDuration,
    );

    useInterval(
        () => {
            if ([RoleEnum.VIEWER].includes(currentRoles[0]) && filteredStageHeaders.length > 0) {
                switchTab(null, tabValue === filteredStageHeaders.length - 1 ? 0 : tabValue + 1);
            }
        },
        !playStageSwitchViewer ? null : currentDuration,
    );

    useEffect(() => {
        if ([StageViewModeEnum.WORK_IN_PROGRESS].includes(stageViewMode)) {
            setIsUnsavedChanges(UtilService.areUnsavedChangesPresent(selectedStage, savedStage));
        }
    }, [selectedStage, stageViewMode, savedStage]);

    useEffect(() => {
        const currentRoles$ = StateService.currentRoles$.subscribe((currentRoles) => {
            setCurrentRoles(currentRoles);
        });
        setInitials('MM');

        return () => {
            currentRoles$.unsubscribe();
        };
    }, []);

    async function getStageHeader(uuid: string) {
        const username = UserService.getUsername();
        const headerArray =
            (await StageService.getStagesForLogin(username, uuid, { signal: abortController.signal })) || ([] as StageHeader[]);
        StateService.stageHeader$.next(headerArray);
    }

    function compareStageValues(triggeredBy: string, index?: number) {
        const newValue = index === undefined ? -1 : index;
        let tempStage = cloneDeep(selectedStage) as Stage;
        if (tempStage.publishedGridConfig) {
            tempStage.publishedGridConfig.layouts.lg = widgets;
        }
        if (newValue !== tabValue && UtilService.areUnsavedChangesPresent(tempStage, savedStage)) {
            setUnsavedChangesNewIndex(index);
            setUnsavedChangesTriggeredBy(triggeredBy);
            StateService.confirmDialogMode$.next(ConfirmDialogModeEnum.UNSAVED_CHANGES);
        } else {
            delegate(triggeredBy, index);
        }
    }

    function delegate(triggeredBy: string, index?: any) {
        if (confirmDialogMode === ConfirmDialogModeEnum.UNSAVED_CHANGES_CONFIRMED && filteredStageHeaders.length > 0) {
            filteredStageHeaders[tabValue].title = savedStage.title;
            filteredStageHeaders[tabValue].status = savedStage.status;
            StateService.stageHeader$.next(filteredStageHeaders);
        }
        StateService.singleValueStyles$.next({} as SingleValueStyles);
        if (triggeredBy === TriggerEnum.ADD_STAGE) {
            addNewStage();
        }
        if (triggeredBy === TriggerEnum.SWITCH_STAGE) {
            switchTab(null, index);
        }
    }

    function preSwitchTab(event, newValue) {
        if (tabValue !== newValue) {
            compareStageValues(TriggerEnum.SWITCH_STAGE, newValue);
        }
    }

    function switchTab(event, newValue) {
        if (tabValue !== newValue) {
            StateService.widgetData$.next(null);
        }
        setIsEditMode(false);
        StateService.selectedHeader$.next(filteredStageHeaders[newValue]);
        StateService.stageViewMode$.next(StageViewModeEnum.PUBLISHED);
        StateService.confirmDialogMode$.next(ConfirmDialogModeEnum.CLOSED);
    }

    function addNewStage() {
        let headerArray = stageHeaders;
        const draftId = uuid.v4();
        const newHeader = {
            draftId: draftId,
            status: StageStatusEnum.NEW,
            title: t('COMMON.NEW_DEFAULT_VALUE'),
        } as StageHeader;
        headerArray.push(newHeader);
        StateService.editorMode$.next(EditorModeEnum.NEW_STAGE);
        StateService.stageViewMode$.next(StageViewModeEnum.WORK_IN_PROGRESS);
        StateService.confirmDialogMode$.next(ConfirmDialogModeEnum.CLOSED);
        StateService.stageHeader$.next(headerArray);
        StateService.selectedHeader$.next(newHeader);
    }

    function editStage() {
        StateService.editorMode$.next(EditorModeEnum.STAGE);
    }

    function callConfirmDialog() {
        StateService.confirmDialogMode$.next(ConfirmDialogModeEnum.DELETE_STAGE);
    }

    function resetSearch() {
        setSearchString('');
        setPreviousSearchString('');
    }

    function fillSearchString(event: any) {
        setSearchString(event?.target?.value?.toLowerCase());
        setPreviousSearchString(event?.target?.value?.toLowerCase());
    }

    function toggleSearchInput() {
        setShowSearchInput(!showSearchInput);
    }

    function addStage() {
        compareStageValues(TriggerEnum.ADD_STAGE);
        setIsEditMode(true);
    }

    function a11yProps(index) {
        return {
            id: `scrollable-auto-tab-${index}`,
            'aria-controls': `scrollable-auto-tabpanel-${index}`,
        };
    }

    function handleStageSwitch() {
        setPlayStageSwitch((prev) => !prev);
    }

    function handleStageSwitchViewer() {
        setPlayStageSwitchViewer((prev) => !prev);
    }

    function openButtonMenu() {
        StateService.stageViewMode$.next(StageViewModeEnum.WORK_IN_PROGRESS);
        setIsEditMode(true);
    }

    function closeButtonMenu() {
        StateService.stageViewMode$.next(StageViewModeEnum.PUBLISHED);
        setIsEditMode(false);
    }

    const saveWorkInProgressData = useCallback(async () => {
        setIsSaving(true);
        await StageService.saveStage(selectedStage, stageHeader, selectedHeader, currentCustomerUuid, StageOperationEnum.SAVE, {
            signal: abortController.signal,
        });
        setIsSaving(false);
        setSavedChanges(true);
        StateService.changesMade$.next(false);
    }, [selectedStage, stageHeader, selectedHeader, currentCustomerUuid, StageOperationEnum.SAVE]);

    async function publishWorkInProgressData() {
        setIsPublishing(true);
        let tempStage = cloneDeep(selectedStage) as Stage;
        tempStage.publishedGridConfig = cloneDeep(tempStage.workInProgressGridConfig);
        tempStage.publishedWidgetData = cloneDeep(tempStage.workInProgressWidgetData);
        tempStage = StageService.setGridLockState(tempStage, tempStage.publishedGridConfig?.layouts?.lg, 'publishedGridConfig', true);
        await StageService.saveStage(tempStage, stageHeader, selectedHeader, currentCustomerUuid, StageOperationEnum.PUBLISH, {
            signal: abortController.signal,
        });
        setIsPublishing(false);
        setSavedChanges(false);
    }

    function resetWorkInProgressToPublishedData() {
        StateService.confirmDialogMode$.next(ConfirmDialogModeEnum.RESET_STAGE_TO_PUBLISH);
        setSavedChanges(false);
    }

    const handleClick = (event: any) => {
        setAnchorEl(event.currentTarget);
        setMenuOpen((prevMenuOpen) => !prevMenuOpen);
    };

    const openSettings = () => {
        StateService.editorMode$.next(EditorModeEnum.SETTINGS);
    };

    const avatarMenuItems = [
        {
            icon: Logout,
            id: 1,
            label: t('NAVIGATION.LOGOUT_LABEL'),
            onClick: UserService.doLogout,
        },
    ];

    const setDarkMode = () => {
        document.querySelector('body').setAttribute('data-theme', 'dark');
        StateService.darkTheme$.next('dark');
        localStorage.setItem('theme', 'dark');
    };

    const setLightMode = () => {
        document.querySelector('body').setAttribute('data-theme', 'light');
        StateService.darkTheme$.next('light');
        localStorage.setItem('theme', 'light');
    };

    const toggleTheme = (e: any) => {
        const checked = e?.target?.checked;
        checked ? setLightMode() : setDarkMode();
        setEvent(e);
        checked && localStorage.setItem('theme', 'light');
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSpecialKeyPress = (event) => {
        if (event.key === 'Escape') {
            setShowViewerHeader((prevState) => !prevState);
        }
    };

    const handleCloseDashboardFilter = () => {
        setSearchString('');
    };

    useEffect(() => {
        document.body.addEventListener('keydown', handleSpecialKeyPress);

        return () => {
            document.body.removeEventListener('keydown', handleSpecialKeyPress);
        };
    }, [showViewerHeader]);

    const getFilterStagesButton = useMemo(() => {
        return (
            <Tooltip
                classes={{ tooltip: classes.tooltip }}
                title={showSearchInput ? t('COMMON.FILTER_STAGES_BUTTON_TOOLTIP_CLOSE') : t('COMMON.FILTER_STAGES_BUTTON_TOOLTIP')}
                placement="left-start"
                TransitionComponent={Fade}
                data-testid="filter-button"
                arrow
            >
                <span>
                    <Button
                        onClick={() => {
                            toggleSearchInput();
                            if (showSearchInput) {
                                handleCloseDashboardFilter();
                            }
                        }}
                        className={classes.headerButton}
                        disabled={disableButtons}
                        variant="outlined"
                        color="primary"
                        style={{
                            borderColor: searchString !== '' ? theme.palette.primary.main : '',
                            color: searchString !== '' ? theme.palette.primary.main : '',
                        }}
                        data-testid="filter-button"
                    >
                        {showSearchInput ? <ClearIcon color="secondary" /> : <Filter color={searchString !== '' ? 'primary' : 'secondary'} />}
                    </Button>
                </span>
            </Tooltip>
        );
    }, [disableButtons, searchString, showSearchInput, t]);

    const getAutoSwitchStagesButton = useMemo(() => {
        return (
            <Tooltip
                classes={{ tooltip: classes.tooltip }}
                title={playStageSwitch ? t('COMMON.SWITCH_STAGES_BUTTON_STOP_TOOLTIP') : t('COMMON.SWITCH_STAGES_BUTTON_TOOLTIP')}
                placement="left-start"
                TransitionComponent={Fade}
                data-testid="switch-stages-button"
                arrow
            >
                <span>
                    <Button
                        onClick={handleStageSwitch}
                        className={[classes.headerButton, classes.autoSwitch].join(' ')}
                        disabled={disableButtons}
                        variant="outlined"
                        color="primary"
                        style={{
                            borderColor: playStageSwitch ? theme.palette.secondary.main : '',
                            color: playStageSwitch ? theme.palette.secondary.main : '',
                        }}
                        data-testid="switch-stages-button"
                    >
                        {playStageSwitch ? <Pause color="primary" /> : <Play color="secondary" />}
                    </Button>
                </span>
            </Tooltip>
        );
    }, [disableButtons, playStageSwitch, t]);

    const getAutoSwitchStagesButtonViewer = useMemo(() => {
        return (
            <Tooltip
                classes={{ tooltip: classes.tooltip }}
                title={playStageSwitchViewer ? t('COMMON.SWITCH_STAGES_BUTTON_STOP_TOOLTIP') : t('COMMON.SWITCH_STAGES_BUTTON_TOOLTIP')}
                placement="left-start"
                TransitionComponent={Fade}
                data-testid="switch-stages-button"
                arrow
            >
                <span>
                    <Button
                        onClick={handleStageSwitchViewer}
                        className={[classes.headerButton, classes.autoSwitch].join(' ')}
                        disabled={disableButtons}
                        variant="outlined"
                        color="primary"
                        style={{
                            borderColor: playStageSwitchViewer ? theme.palette.secondary.main : '',
                            color: playStageSwitchViewer ? theme.palette.secondary.main : '',
                        }}
                        data-testid="switch-stages-button"
                    >
                        {playStageSwitchViewer ? <Pause color="primary" /> : <Play color="secondary" />}
                    </Button>
                </span>
            </Tooltip>
        );
    }, [disableButtons, playStageSwitchViewer, t]);

    const getAddStageButton = useMemo(() => {
        return (
            rolesPresent &&
            DoormanService.isAccessGranted('addStageButton', currentRoles) && (
                <Tooltip
                    classes={{ tooltip: classes.tooltip }}
                    title={t('COMMON.STAGE_ADD_BUTTON')}
                    placement="left-start"
                    TransitionComponent={Fade}
                    data-testid="edit-button"
                    arrow
                >
                    <span>
                        <Button
                            variant="outlined"
                            onClick={addStage}
                            className={classes.headerButton}
                            disabled={disableButtons}
                            data-testid="add-stage-button"
                            color="primary"
                        >
                            <Add />
                        </Button>
                    </span>
                </Tooltip>
            )
        );
    }, [rolesPresent, currentRoles, disableButtons, filteredStageHeaders, savedStage, selectedStage, t]);

    const getEditStageButton = useMemo(() => {
        return (
            rolesPresent &&
            DoormanService.isAccessGranted('addStageButton', currentRoles) && (
                <Tooltip
                    classes={{ tooltip: classes.tooltip }}
                    title={t('COMMON.STAGE_EDIT_TOOLTIP')}
                    placement="left-start"
                    TransitionComponent={Fade}
                    data-testid="edit-button"
                    arrow
                >
                    <span>
                        <Button
                            variant="outlined"
                            onClick={openButtonMenu}
                            className={classes.headerButton}
                            disabled={disableButtons}
                            data-testid="edit-button"
                            color="primary"
                        >
                            <Edit />
                        </Button>
                    </span>
                </Tooltip>
            )
        );
    }, [rolesPresent, currentRoles, disableButtons, filteredStageHeaders, savedStage, selectedStage, t]);

    const getEditOffStageButton = useMemo(() => {
        return (
            <Tooltip
                classes={{ tooltip: classes.tooltip }}
                title={t('COMMON.STAGE_EDIT_CLOSE_TOOLTIP')}
                placement="left-start"
                TransitionComponent={Fade}
                data-testid="edit-button"
                arrow
            >
                <span>
                    <Button
                        variant="outlined"
                        onClick={closeButtonMenu}
                        className={classes.headerButton}
                        disabled={disableButtons}
                        data-testid="edit-button"
                        color="primary"
                    >
                        <EditOff />
                    </Button>
                </span>
            </Tooltip>
        );
    }, [rolesPresent, currentRoles, disableButtons, filteredStageHeaders, savedStage, selectedStage, t]);

    const getSaveStageButton = useMemo(() => {
        return (
            <Tooltip
                classes={{ tooltip: classes.tooltip }}
                title={t('COMMON.STAGE_SAVE_TOOLTIP')}
                placement="left-start"
                TransitionComponent={Fade}
                data-testid="save-button"
                arrow
            >
                <Button color="primary" className={classes.headerButton} size="medium" onClick={saveWorkInProgressData} variant="outlined">
                    <Save />
                </Button>
            </Tooltip>
        );
    }, [rolesPresent, currentRoles, disableButtons, filteredStageHeaders, savedStage, selectedStage, t]);

    const getPublishStageButton = useMemo(() => {
        return (
            <Tooltip
                classes={{ tooltip: classes.tooltip }}
                title={t('COMMON.STAGE_PUBLISH_TOOLTIP')}
                placement="left-start"
                TransitionComponent={Fade}
                data-testid="publish-button"
                arrow
            >
                <Button
                    color="primary"
                    className={classes.headerButton}
                    size="medium"
                    onClick={publishWorkInProgressData}
                    variant="outlined"
                >
                    <ArrowUpRight />
                </Button>
            </Tooltip>
        );
    }, [rolesPresent, currentRoles, disableButtons, filteredStageHeaders, savedStage, selectedStage, t]);

    const getResetToPublishedStageButton = useMemo(() => {
        return (
            <Tooltip
                classes={{ tooltip: classes.tooltip }}
                title={t('COMMON.STAGE_RESET_TOOLTIP')}
                placement="left-start"
                TransitionComponent={Fade}
                data-testid="publish-button"
                arrow
            >
                <Button
                    color="primary"
                    className={classes.headerButton}
                    size="medium"
                    onClick={resetWorkInProgressToPublishedData}
                    variant="outlined"
                >
                    <Reset />
                </Button>
            </Tooltip>
        );
    }, [rolesPresent, currentRoles, disableButtons, filteredStageHeaders, savedStage, selectedStage, t]);

    const getHeaderContent = useMemo(() => {
        return (
            <div className={classes.buttonWrapper}>
                <div className={classes.tabButton} style={{ paddingRight: '12px' }} onClick={editStage} data-testid="stage-editing">
                    <Tooltip
                        classes={{ tooltip: classes.tooltip }}
                        title={t('COMMON.STAGE_SETTINGS_TOOLTIP')}
                        placement="left-start"
                        TransitionComponent={Fade}
                        arrow
                    >
                        <div className={classes.fakeButton} data-testid="edit_stage">
                            <Edit />
                        </div>
                    </Tooltip>
                </div>
                <div className={classes.tabButton} onClick={callConfirmDialog}>
                    <Tooltip
                        classes={{ tooltip: classes.tooltip }}
                        title={t('COMMON.STAGE_DELETE_TOOLTIP')}
                        placement="left-start"
                        TransitionComponent={Fade}
                        arrow
                    >
                        <div className={classes.fakeButton} data-testid="delete_stage">
                            <TrashCan />
                        </div>
                    </Tooltip>
                </div>
            </div>
        );
    }, [t]);

    const generateTabs = useMemo(() => {
        return filteredStageHeaders?.map((tab, index) => {
            return (
                <StageHeaderTab
                    className={classes.tab}
                    key={uuid.v4()}
                    id={index}
                    label={
                        <Badge
                            data-testid="badge"
                            color="primary"
                            variant="dot"
                            invisible={
                                currentRoles.includes(RoleEnum.VIEWER) ||
                                [StageStatusEnum.NEW, StageStatusEnum.PUBLISHED].includes(tab.status)
                            }
                            className={classes.badge}
                            overlap="rectangular"
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <div className={classes.wholeTab}>
                                {tab.title.length > 15 ? (
                                    <Tooltip
                                        classes={{ tooltip: classes.tooltip }}
                                        title={tab.title}
                                        placement="left-start"
                                        TransitionComponent={Fade}
                                        arrow
                                    >
                                        <div className={classes.stageTitle}>{tab.title}</div>
                                    </Tooltip>
                                ) : (
                                    <div className={classes.stageTitle}>{tab.title}</div>
                                )}
                                {tabValue === index &&
                                    [StageViewModeEnum.WORK_IN_PROGRESS].includes(stageViewMode) &&
                                    rolesPresent &&
                                    DoormanService.isAccessGranted('stageEditing', currentRoles) && <>{getHeaderContent}</>}
                            </div>
                        </Badge>
                    }
                    {...a11yProps(0)}
                />
            );
        });
    }, [filteredStageHeaders, stageViewMode, tabValue, currentRoles]);

    const handleLanguageChange = (event: any) => {
        const value = event.target.value as string;
        i18next.changeLanguage(value);
        saveLoginSettings(value);
    };

    async function saveLoginSettings(savedLanguage: string) {
        localStorage.setItem('language', savedLanguage);
        setCurrentLanguage(savedLanguage);
        setNewLanguage(savedLanguage);
        setIsLoginSettingsSaving(true);
        const response = await SettingsService.patchLoginSettings(
            { ...loginSettings, language: savedLanguage, duration: currentDuration },
            currentCustomerUuid,
            UserService.getUsername(),
            { signal: abortController.signal },
        );
        if (response?.status === 204) {
            StateService.loginSettings$.next(loginSettings);
        }
        setIsLoginSettingsSaving(false);
        setLatestDuration(currentDuration);
    }

    return (
        <>
            {[RoleEnum.VIEWER].includes(currentRoles[0]) ? (
                <div className={showViewerHeader ? `${classes.root} ${classes.slideIn}` : `${classes.root} ${classes.slideOut}`}>
                    <div className={classes.bar}>
                        <AppBar className={classes.appBar} position="static" color="default">
                            <>
                                {isLoading && <BlockUi />}
                                {!isLoading && (
                                    <StageHeaderTabs value={tabValue} onChange={preSwitchTab}>
                                        {generateTabs}
                                    </StageHeaderTabs>
                                )}
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {[StageViewModeEnum.PUBLISHED].includes(stageViewMode) && (
                                        <>
                                            {getAutoSwitchStagesButtonViewer}
                                            {getFilterStagesButton}
                                        </>
                                    )}

                                    {stageHeaders.length > 1 && (
                                        <>
                                            {[StageViewModeEnum.PUBLISHED].includes(stageViewMode) && showSearchInput && (
                                                <TextField
                                                    size="small"
                                                    variant="outlined"
                                                    placeholder={t(`STAGE_GROUPING.SEARCH_DASHBOARD_TEXT`)}
                                                    onChange={fillSearchString}
                                                    value={searchString}
                                                    className={classes.stageFilterInput}
                                                    inputProps={{ 'data-testid': 'filter-stages' }}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <Tooltip
                                                                    classes={{
                                                                        tooltip: classes.tooltip,
                                                                    }}
                                                                    title={t('COMMON.FILTER_STAGES_BUTTON_TOOLTIP_REMOVE')}
                                                                    placement="left-start"
                                                                    TransitionComponent={Fade}
                                                                    arrow
                                                                >
                                                                    <DeleteOutlineIcon
                                                                        className={classes.searchAdornment}
                                                                        onClick={resetSearch}
                                                                    />
                                                                </Tooltip>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}
                                    <Divider orientation="vertical" flexItem className={classes.divider} />

                                    <div>
                                        <Avatar className={classes.avatar} onClick={handleClick} data-testid="global-header-avatar">
                                            <span className={classes.lh1} style={{ marginTop: '2px' }}>
                                                {initials}
                                            </span>
                                            {menuOpen && (
                                                <Menu
                                                    classes={{ paper: classes.menu__wrapper }}
                                                    anchorEl={anchorEl}
                                                    keepMounted
                                                    open={Boolean(anchorEl)}
                                                    onClose={handleClose}
                                                    anchorOrigin={{
                                                        vertical: 60,
                                                        horizontal: -40,
                                                    }}
                                                    transformOrigin={{
                                                        vertical: -10,
                                                        horizontal: 'center',
                                                    }}
                                                    data-testid="menu"
                                                >
                                                    <FormControl size="small" classes={{ root: classes.languageSwitcher }}>
                                                        <Select
                                                            data-testid="language"
                                                            value={localStorageLang}
                                                            onChange={handleLanguageChange}
                                                            inputProps={{
                                                                name: 'language',
                                                                id: 'language',
                                                                style: { paddingLeft: 8, height: '32px !important' },
                                                            }}
                                                            IconComponent={ChevronDown}
                                                        >
                                                            <MenuItem value="de" className={classes.dropDownOption}>
                                                                {t('LANGUAGES.DE_DROPDOWN')}
                                                            </MenuItem>
                                                            <MenuItem value="en" className={classes.dropDownOption}>
                                                                {t('LANGUAGES.EN_DROPDOWN')}
                                                            </MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                    {avatarMenuItems.map((item) => (
                                                        <MenuItem onClick={handleClose} key={item.id} className={classes.menuItems}>
                                                            <div
                                                                className={`classes.menu__${item.label}`}
                                                                style={{ width: '100%', fontSize: '16px' }}
                                                            >
                                                                <div className={classes.item} onClick={() => item.onClick()}>
                                                                    {item.icon && <item.icon />}
                                                                    {item.label}
                                                                </div>
                                                            </div>
                                                        </MenuItem>
                                                    ))}
                                                    <Divider />
                                                    <MenuItem className={classes.menuItems} data-testid="theme-toggle">
                                                        <div className={classes.themeToggleHeadline}>
                                                            {t('CUSTOMER_SETTINGS.THEME_TOGGLE_HEADLINE')}
                                                        </div>
                                                        <ThemeToggle toggleTheme={toggleTheme} bodyTheme={bodyTheme} />
                                                    </MenuItem>
                                                </Menu>
                                            )}
                                        </Avatar>
                                    </div>
                                </div>
                            </>
                        </AppBar>
                    </div>
                </div>
            ) : (
                <div className={classes.root}>
                    <div className={classes.bar}>
                        <AppBar className={classes.appBar} position="static" color="default">
                            <>
                                {isLoading && <BlockUi />}
                                {!isLoading && (
                                    <StageHeaderTabs value={tabValue} onChange={preSwitchTab}>
                                        {generateTabs}
                                    </StageHeaderTabs>
                                )}
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {!isEditMode && getAddStageButton}

                                    {!isEditMode ? getEditStageButton : getEditOffStageButton}

                                    {isUnsavedChanges && isEditMode && getResetToPublishedStageButton}

                                    {(isUnsavedChanges || [StageStatusEnum.NEW].includes(selectedStage?.status)) &&
                                        isEditMode &&
                                        isChangeMade &&
                                        getSaveStageButton}

                                    {[StageStatusEnum.SAVED, StageStatusEnum.PUBLISHED_AND_EDITED].includes(selectedStage?.status) &&
                                        [RoleEnum.SUPER_USER, RoleEnum.ADMIN].includes(currentRoles[0]) &&
                                        !isUnsavedChanges &&
                                        [StageViewModeEnum.WORK_IN_PROGRESS].includes(stageViewMode) &&
                                        getPublishStageButton}

                                    {[StageViewModeEnum.PUBLISHED].includes(stageViewMode) && (
                                        <Divider orientation="vertical" flexItem className={classes.divider} />
                                    )}

                                    {[StageViewModeEnum.PUBLISHED].includes(stageViewMode) && (
                                        <>
                                            {getAutoSwitchStagesButton}
                                            {getFilterStagesButton}
                                        </>
                                    )}

                                    {stageHeaders.length > 1 && (
                                        <>
                                            {[StageViewModeEnum.PUBLISHED].includes(stageViewMode) && showSearchInput && (
                                                <TextField
                                                    size="small"
                                                    variant="outlined"
                                                    placeholder={t(`STAGE_GROUPING.SEARCH_DASHBOARD_TEXT`)}
                                                    onChange={fillSearchString}
                                                    value={searchString}
                                                    className={classes.stageFilterInput}
                                                    inputProps={{ 'data-testid': 'filter-stages' }}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <Tooltip
                                                                    classes={{
                                                                        tooltip: classes.tooltip,
                                                                    }}
                                                                    title={t('COMMON.FILTER_STAGES_BUTTON_TOOLTIP_REMOVE')}
                                                                    placement="left-start"
                                                                    TransitionComponent={Fade}
                                                                    arrow
                                                                >
                                                                    <DeleteOutlineIcon
                                                                        className={classes.searchAdornment}
                                                                        onClick={resetSearch}
                                                                    />
                                                                </Tooltip>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                            </>
                        </AppBar>
                    </div>
                </div>
            )}
        </>
    );
}

export default Header;
